// The one-card pages of the loan officer one-pager: processing, processing
// failed, syncing, waiting on Encompass, sync failed, synced.

import React from "react";
import {
  CheckOutlined,
  CloseCircleOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  LoadingOutlined,
  LockOutlined,
  RedoOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { LODocument, LOStatus } from "./loOnePagerClient";
import { TranslateLO } from "./loOnePagerI18n";

export const fileIcon = (name: string) => {
  const n = `${name || ""}`.toLowerCase();
  if (n.endsWith(".pdf")) return <FilePdfOutlined rev={undefined} />;
  if (/\.(jpe?g|png|gif|webp|heic|heif|tiff?)$/.test(n)) return <FileImageOutlined rev={undefined} />;
  return <FileOutlined rev={undefined} />;
};

export const formatWhen = (iso?: string, lang?: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleString(lang === "zh" ? "zh-CN" : lang === "es" ? "es-US" : "en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (e) {
    return d.toLocaleString();
  }
};

const personName = (p?: { first_name: string; last_name: string } | null) =>
  p ? [p.first_name, p.last_name].filter(Boolean).join(" ") : "";

const DocumentGroups: React.FC<{ status: LOStatus; t: TranslateLO }> = ({ status, t }) => {
  const groups: { title: string; docs: LODocument[] }[] = [];
  const borrower = status.documents.filter((d) => d.forwhom !== "coborrower");
  const co = status.documents.filter((d) => d.forwhom === "coborrower");
  if (status.coborrower) {
    groups.push({
      title: t("personDocs", { name: personName(status.borrower), role: t("roleBorrower"), n: borrower.length }),
      docs: borrower,
    });
    groups.push({
      title: t("personDocs", { name: personName(status.coborrower), role: t("roleCoborrower"), n: co.length }),
      docs: co,
    });
  } else {
    groups.push({ title: t("docsUploaded", { n: borrower.length }).toUpperCase(), docs: borrower });
  }
  if (status.documents.length === 0) {
    return <div className="lop-note">{t("noDocuments")}</div>;
  }
  return (
    <>
      {groups
        .filter((g) => g.docs.length > 0)
        .map((g) => (
          <div key={g.title}>
            <div className="lop-doc-group-title">{g.title}</div>
            <div className="lop-doc-grid">
              {g.docs.map((d) => (
                <div key={d.serialid} className="lop-doc-chip" title={d.filename}>
                  {fileIcon(d.filename)}
                  <span>{d.filename}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
    </>
  );
};

export const ProcessingView: React.FC<{ status: LOStatus; t: TranslateLO }> = ({ status, t }) => (
  <div className="lop-card-wrap">
    <div className="lop-card">
      <div className="lop-card-head">
        <div className="lop-card-icon">
          <LoadingOutlined rev={undefined} spin />
        </div>
        <div className="lop-card-title">{t("processingTitle")}</div>
        <div className="lop-card-lede">{t("processingLede")}</div>
        <div className="lop-indeterminate" />
      </div>
      <div className="lop-card-body">
        <DocumentGroups status={status} t={t} />
      </div>
      <div className="lop-card-foot">{t("leaveOpen")}</div>
    </div>
  </div>
);

export const ProcessingFailedView: React.FC<{
  status: LOStatus;
  t: TranslateLO;
  busy: boolean;
  onRetry: () => void;
  onSkip: () => void;
}> = ({ status, t, busy, onRetry, onSkip }) => (
  <div className="lop-card-wrap">
    <div className="lop-card">
      <div className="lop-card-head">
        <div className="lop-card-icon is-warn">
          <WarningOutlined rev={undefined} />
        </div>
        <div className="lop-card-title">{t("pfTitle")}</div>
        <div className="lop-card-lede">{t("pfLede")}</div>
      </div>
      <div className="lop-card-body">
        <DocumentGroups status={status} t={t} />
        {status.prefill?.error ? (
          <div className="lop-error-box">
            <pre>{status.prefill.error}</pre>
          </div>
        ) : null}
        <div className="lop-note">{t("pfNote")}</div>
      </div>
      <div className="lop-card-actions">
        <button type="button" className="lop-btn-secondary" disabled={busy} onClick={onSkip}>
          {t("continueWithout")}
        </button>
        <button type="button" className="lop-btn-primary" disabled={busy} onClick={onRetry}>
          <RedoOutlined rev={undefined} />
          {t("tryAgain")}
        </button>
      </div>
    </div>
  </div>
);

export const SyncingView: React.FC<{ t: TranslateLO }> = ({ t }) => (
  <div className="lop-card-wrap">
    <div className="lop-card">
      <div className="lop-card-head">
        <div className="lop-card-icon">
          <LoadingOutlined rev={undefined} spin />
        </div>
        <div className="lop-card-title">{t("syncingTitle")}</div>
        <div className="lop-card-lede">{t("syncingLede")}</div>
        <div className="lop-indeterminate" />
      </div>
      <div className="lop-card-foot">{t("syncingNote")}</div>
    </div>
  </div>
);

export const SyncWaitingView: React.FC<{
  status: LOStatus;
  t: TranslateLO;
  lang: string;
  busy: boolean;
  onRetry: () => void;
}> = ({ status, t, lang, busy, onRetry }) => {
  const job = status.sync;
  const locked = job?.error_kind === "lock";
  return (
    <div className="lop-card-wrap">
      <div className="lop-card">
        <div className="lop-card-head">
          <div className="lop-card-icon is-warn">{locked ? <LockOutlined rev={undefined} /> : <LoadingOutlined rev={undefined} spin />}</div>
          <div className="lop-card-title">{locked ? t("lockedTitle") : t("waitingTitle")}</div>
          <div className="lop-card-lede">{locked ? t("lockedLede") : t("waitingLede")}</div>
        </div>
        <div className="lop-card-body">
          <div className="lop-facts">
            <div className="lop-fact">
              <span className="lop-fact-label">{t("lastAttempt")}</span>
              <span className="lop-fact-value">{formatWhen(job?.last_attempted_at, lang) || "—"}</span>
            </div>
            <div className="lop-fact">
              <span className="lop-fact-label">{t("nextAttempt")}</span>
              <span className="lop-fact-value">{formatWhen(job?.next_retry_at, lang) || "—"}</span>
            </div>
            <div className="lop-fact">
              <span className="lop-fact-label">{t("attemptsSoFar")}</span>
              <span className="lop-fact-value">{job?.attempts ?? 0}</span>
            </div>
          </div>
          {job?.error ? (
            <div className="lop-error-box">
              <div className="gop-eyebrow">{t("errorFrom")}</div>
              <pre>{job.error}</pre>
            </div>
          ) : null}
          <div className="lop-note">{t("retryNote")}</div>
        </div>
        <div className="lop-card-foot">{t("lockedFoot")}</div>
        <div className="lop-card-actions">
          <button type="button" className="lop-btn-primary" disabled={busy} onClick={onRetry}>
            <RedoOutlined rev={undefined} />
            {t("retryNow")}
          </button>
        </div>
      </div>
    </div>
  );
};

export const SyncFailedView: React.FC<{
  status: LOStatus;
  t: TranslateLO;
  lang: string;
  busy: boolean;
  onRetry: () => void;
  onBack: () => void;
}> = ({ status, t, lang, busy, onRetry, onBack }) => (
  <div className="lop-card-wrap">
    <div className="lop-card">
      <div className="lop-card-head">
        <div className="lop-card-icon is-error">
          <CloseCircleOutlined rev={undefined} />
        </div>
        <div className="lop-card-title">{t("failedTitle")}</div>
        <div className="lop-card-lede">{t("failedLede")}</div>
      </div>
      <div className="lop-card-body">
        <div className="lop-error-box">
          <div className="gop-eyebrow">{t("errorFrom")}</div>
          <pre>{status.sync?.error || "—"}</pre>
        </div>
        {status.sync?.last_attempted_at ? (
          <div className="lop-note">{t("failedAt", { time: formatWhen(status.sync.last_attempted_at, lang) })}</div>
        ) : null}
      </div>
      <div className="lop-card-actions">
        <button type="button" className="lop-btn-secondary" disabled={busy} onClick={onBack}>
          {t("backToReview")}
        </button>
        <button type="button" className="lop-btn-primary" disabled={busy} onClick={onRetry}>
          <RedoOutlined rev={undefined} />
          {t("retrySync")}
        </button>
      </div>
    </div>
  </div>
);

// Encompass sandbox login for testers, shown on the synced page only when
// the loan went to that instance. Test accounts on a developer sandbox, not
// production credentials; keyed by instance id so a production instance
// never matches.
const SANDBOX_TESTING_INFO: Record<string, { instance: string; username: string; password: string }> = {
  DEBE11136104: { instance: "DEBE11136104", username: "azhang", password: "P@$$w0rd" },
};

export const SyncedView: React.FC<{ status: LOStatus; t: TranslateLO; lang: string; onStartAnother: () => void }> = ({
  status,
  t,
  lang,
  onStartAnother,
}) => {
  const names = [personName(status.borrower), personName(status.coborrower)].filter(Boolean).join(t("and"));
  const testing = SANDBOX_TESTING_INFO[status.encompass_instance_id || ""];
  return (
    <div className="lop-card-wrap">
      <div className="lop-card">
        <div className="lop-card-head">
          <div className="lop-card-icon is-success">
            <CheckOutlined rev={undefined} />
          </div>
          <div className="lop-card-title">{t("syncedTitle")}</div>
          <div className="lop-card-lede">{t("syncedLede", { names })}</div>
        </div>
        <div className="lop-card-body">
          <div className="lop-facts">
            {/* The Encompass GUID is deliberately not shown: it is the API's
                handle and means nothing in the Encompass UI. */}
            {status.encompass_loan_number ? (
              <div className="lop-fact">
                <span className="lop-fact-label">{t("encompassLoanNumber")}</span>
                <span className="lop-fact-value">{status.encompass_loan_number}</span>
              </div>
            ) : null}
            <div className="lop-fact">
              <span className="lop-fact-label">{t("encompassFolder")}</span>
              <span className="lop-fact-value">{status.encompass_loan_folder || "My Pipeline"}</span>
            </div>
            <div className="lop-fact">
              <span className="lop-fact-label">{t("docsAttached")}</span>
              <span className="lop-fact-value">{status.attached_count}</span>
            </div>
            <div className="lop-fact">
              <span className="lop-fact-label">{t("syncedAt")}</span>
              <span className="lop-fact-value">{formatWhen(status.sync?.completed_at, lang) || "—"}</span>
            </div>
          </div>
          <div className="lop-note">
            {t("inEncompassNote", {
              folder: status.encompass_loan_folder || "My Pipeline",
              by: status.encompass_loan_number ? t("byLoanNumber") : t("byBorrowerName"),
            })}
          </div>
          {status.consumer_connect?.sent_to ? (
            <div className="lop-note">{t("ccSentTo", { email: status.consumer_connect.sent_to })}</div>
          ) : null}
          {testing ? (
            <div className="lop-testing-box">
              <div className="lop-testing-title">{t("testingInfoTitle")}</div>
              <div className="lop-testing-row">
                <span>{t("testingInstance")}</span>
                <code>{testing.instance}</code>
              </div>
              <div className="lop-testing-row">
                <span>{t("testingUsername")}</span>
                <code>{testing.username}</code>
              </div>
              <div className="lop-testing-row">
                <span>{t("testingPassword")}</span>
                <code>{testing.password}</code>
              </div>
              <div className="lop-testing-note">{t("testingNote")}</div>
            </div>
          ) : null}
        </div>
        <div className="lop-card-actions">
          <button type="button" className="lop-btn-secondary" onClick={onStartAnother}>
            {t("startAnother")}
          </button>
        </div>
      </div>
    </div>
  );
};
